import { useState, useEffect, useCallback } from 'react';
import { submitAttempt } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './WordForge.css';

const KEYS = [
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L'],
  ['ENTER','Z','X','C','V','B','N','M','⌫']
];

export default function WordForge({ puzzle, onSolve, alreadyAttempted, onPlayAgain }) {
  const { user, fetchCredits } = useAuth();
  const [board, setBoard]           = useState(Array(6).fill('').map(() => Array(5).fill('')));
  const [results, setResults]       = useState(Array(6).fill(null));
  const [currentRow, setCurrentRow] = useState(0);
  const [currentCol, setCurrentCol] = useState(0);
  const [keyStates, setKeyStates]   = useState({});
  const [message, setMessage]       = useState('');
  const [gameOver, setGameOver]     = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [shakingRow, setShakingRow] = useState(-1);
  const [hintLevel, setHintLevel]   = useState(0);
  const [showHints, setShowHints]   = useState(false);
  const [solved, setSolved]         = useState(false);
  const [shareText, setShareText]   = useState('');

  const SECRET = puzzle?.content?.word || '';

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2000);
  };

  const scoreGuess = (guess, secret) => {
    const result = Array(5).fill('absent');
    const sArr = secret.split('');
    const gArr = guess.split('');
    for (let i = 0; i < 5; i++) {
      if (gArr[i] === sArr[i]) { result[i] = 'correct'; sArr[i] = null; gArr[i] = null; }
    }
    for (let i = 0; i < 5; i++) {
      if (gArr[i] && sArr.includes(gArr[i])) {
        result[i] = 'present';
        sArr[sArr.indexOf(gArr[i])] = null;
      }
    }
    return result;
  };

  const buildShareText = (guessNum, won, allResults, timeSecs) => {
    const map = { correct: '🟩', present: '🟨', absent: '⬛' };
    const theme = puzzle?.content?.theme_emoji && puzzle?.content?.theme
      ? `${puzzle.content.theme_emoji} ${puzzle.content.theme} Week\n` : '';
    const timeStr = timeSecs ? `${Math.floor(timeSecs/60) > 0 ? Math.floor(timeSecs/60)+'m ' : ''}${timeSecs%60}s` : '';
    let t = `${theme}🧩 WordForge #${puzzle.puzzle_num}\n`;
    t += won ? `${guessNum}/6 tries · ${timeStr} ✅` : `X/6 ❌ · ${timeStr}`;
    t += '\n\n';
    allResults.forEach(r => { if (r) t += r.map(s => map[s]).join('') + '\n'; });
    t += `\n🎮 Play today → puzzleforge.io`;
    return t;
  };

  const submitGuess = useCallback(() => {
    if (gameOver) return;
    const guess = board[currentRow].join('');
    if (guess.length < 5) { showMsg('Not enough letters'); return; }

    const result = scoreGuess(guess, SECRET);
    const newResults = [...results];
    newResults[currentRow] = result;
    setResults(newResults);

    const priority = { correct: 3, present: 2, absent: 1 };
    const newKeys = { ...keyStates };
    guess.split('').forEach((letter, i) => {
      const state = result[i];
      if ((priority[state] || 0) > (priority[newKeys[letter]] || 0)) newKeys[letter] = state;
    });
    setKeyStates(newKeys);

    const won = guess === SECRET;
    const isLast = currentRow === 5;

    if (won || isLast) {
      setGameOver(true);
      setSolved(won);
      const guessNum = currentRow + 1;
      const timeTaken = Math.floor((Date.now() - (puzzle._startTime || Date.now())) / 1000);
      setShareText(buildShareText(guessNum, won, newResults, timeTaken));
      if (user) {
        submitAttempt({
          puzzle_id: puzzle.id,
          guesses: board.slice(0, guessNum).map(r => r.join('')),
          solved: won,
          num_guesses: guessNum,
          score: won ? Math.max(10, 60 - guessNum * 8) : 0,
        }).then(() => { if (fetchCredits) fetchCredits(); }).catch(console.error);
      }
      if (!won) {
        setShakingRow(currentRow);
        setTimeout(() => setShakingRow(-1), 500);
      }
      setTimeout(() => setShowResult(true), 1600);
      if (onSolve) onSolve(won, board.slice(0, currentRow + 1).map(r => r.join('')), won ? Math.max(10, 60 - (currentRow + 1) * 8) : 0, null);
    }

    setCurrentRow(r => r + 1);
    setCurrentCol(0);
  }, [board, currentRow, gameOver, SECRET, keyStates, results, user, puzzle, onSolve, fetchCredits]);

  const handleKey = useCallback((k) => {
    if (gameOver) return;
    if (k === '⌫' || k === 'BACKSPACE') {
      if (currentCol > 0) {
        const newBoard = board.map(r => [...r]);
        newBoard[currentRow][currentCol - 1] = '';
        setBoard(newBoard);
        setCurrentCol(c => c - 1);
      }
    } else if (k === 'ENTER') {
      submitGuess();
    } else if (/^[A-Z]$/.test(k) && currentCol < 5) {
      const newBoard = board.map(r => [...r]);
      newBoard[currentRow][currentCol] = k;
      setBoard(newBoard);
      setCurrentCol(c => c + 1);
    }
  }, [board, currentRow, currentCol, gameOver, submitGuess]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Backspace') handleKey('BACKSPACE');
      else if (e.key === 'Enter') handleKey('ENTER');
      else if (/^[a-zA-Z]$/.test(e.key)) handleKey(e.key.toUpperCase());
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleKey]);

  const getCellClass = (row, col) => {
    const classes = ['wf-cell'];
    if (results[row]) classes.push(results[row][col]);
    else if (board[row][col]) classes.push('filled');
    if (row === currentRow && !results[row]) classes.push('active-row');
    if (row === 0 && !results[0] && !board[0][0]) classes.push('first-row');
    return classes.join(' ');
  };

  const copy = () => {
    navigator.clipboard.writeText(shareText).catch(() => {});
    showMsg('Copied!');
  };

  if (!SECRET) return <div className="wf-loading">Loading puzzle...</div>;

  if (alreadyAttempted && !gameOver) return (
    <div className="wf-loading" style={{textAlign:'center', padding:'40px 20px'}}>
      <div style={{fontSize:'32px', marginBottom:'12px'}}><span style={{color:'initial'}}>✅</span></div>
      <div style={{fontSize:'16px', fontWeight:'700', color:'#f0f4ff', marginBottom:'8px'}}>Already completed today!</div>
      <div style={{fontSize:'12px', color:'rgba(240,244,255,0.5)', marginBottom:'16px'}}>Use 1 credit to play a new puzzle.</div>
      {onPlayAgain
        ? <button onClick={onPlayAgain} style={{fontSize:'12px', color:'#4fe3c1', background:'rgba(79,227,193,0.1)', border:'1px solid rgba(79,227,193,0.35)', borderRadius:'8px', padding:'8px 16px', cursor:'pointer'}}>✨ Play again — 1 credit</button>
        : <a href="/upgrade" style={{fontSize:'12px', color:'#e8c547', textDecoration:'none'}}>Get more credits →</a>
      }
    </div>
  );

  return (
    <div className="wf-wrap">

      {/* ── 1. Title — compact ── */}
      <div className="wf-header">
        <h2 className="wf-title">WordForge</h2>
        <span className="wf-sub mono">5-letter word · 6 tries</span>
      </div>

      {/* ── 2. Grid — full width, immediate ── */}
      <div className="wf-grid">
        {board.map((row, ri) => (
          <div key={ri} className={`wf-row ${shakingRow === ri ? 'shake' : ''}`}>
            {row.map((letter, ci) => (
              <div
                key={ci}
                className={getCellClass(ri, ci)}
                style={results[ri] ? { animationDelay: `${ci * 0.28}s` } : {}}
              >
                {ri === 0 && !results[0] && !board[0][0] && ci === 2 && !letter
                  ? <span className="wf-tap-hint">TYPE</span>
                  : letter}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ── 3. Message ── */}
      {message && <div className="wf-message mono">{message}</div>}

      {/* ── 4. Keyboard ── */}
      <div className="wf-keyboard">
        {KEYS.map((row, ri) => (
          <div key={ri} className="wf-key-row">
            {row.map(k => (
              <button
                key={k}
                className={`wf-key mono ${keyStates[k] || ''} ${k.length > 1 ? 'wide' : ''}`}
                onClick={() => handleKey(k)}
              >
                {k}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* ── 5. Theme + fun fact — BELOW keyboard, hidden when hints shown ── */}
      {!gameOver && !showHints && (puzzle?.content?.theme || puzzle?.content?.fun_fact) && (
        <div className="wf-engage">
          {puzzle?.content?.theme && (
            <div className="wf-engage-theme mono">
              {puzzle.content.theme_emoji} <strong>{puzzle.content.theme}</strong> Week
            </div>
          )}
          {puzzle?.content?.fun_fact && (
            <div className="wf-engage-fact mono">💡 {puzzle.content.fun_fact}</div>
          )}
        </div>
      )}

      {/* ── 6. Hints — below theme ── */}
      {!gameOver && puzzle?.content?.hints?.length > 0 && (
        <div className="wf-hints-wrap">
          <button
            className="wf-hint-btn mono"
            onClick={() => { setShowHints(true); setHintLevel(h => Math.min(h + 1, puzzle.content.hints.length)); }}
            disabled={hintLevel >= puzzle.content.hints.length}
          >
            💡 {hintLevel === 0 ? 'Get a hint' : hintLevel >= puzzle.content.hints.length ? 'Final hint shown' : 'Next hint'}
          </button>
          {showHints && hintLevel > 0 && (
            <div className="wf-hints-list">
              {puzzle.content.hints.slice(0, hintLevel).map((hint, i) => (
                <div key={i} className="wf-hint mono">
                  <span className="wf-hint-num">{i + 1}</span> {hint}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 7. Result overlay ── */}
      {showResult && (
        <div className="wf-overlay" onClick={(e) => e.target === e.currentTarget && setShowResult(false)}>
          <div className="wf-result">
            <div className="result-icon">{solved ? (currentRow <= 2 ? '🏆' : '🎉') : '😤'}</div>
            <h3 className="result-title">
              {solved
                ? ['', 'Genius!', 'Magnificent!', 'Impressive!', 'Splendid!', 'Great!', 'Phew!'][currentRow]
                : 'Better luck next time!'}
            </h3>
            <div className="result-word mono">{SECRET}</div>
            {puzzle.content.fun_fact && (
              <p className="result-fact">{puzzle.content.fun_fact}</p>
            )}
            {puzzle.content.story && (
              <div className="result-story mono">
                <span className="result-story-label">📖 Word Story</span>
                <p>{puzzle.content.story}</p>
              </div>
            )}
            <div className="share-preview mono">{shareText}</div>
            <button className="btn-copy mono" onClick={copy}>Copy & Share</button>
            {onPlayAgain && (
              <button className="btn-play-again mono" onClick={onPlayAgain}>
                ✨ Play again — 1 credit
              </button>
            )}
            {!user && (
              <p className="result-cta">
                <a href="/register">Sign up free</a> to track your streak 🔥
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
